<template>
  <WInput
    v-model="date"
    type="date"
    name="date-picker"
    class="widget-date-time-input"
    :postfix="themeIcons.calendar"
    :max="max"
    :min="dayjs().subtract(20, 'year').format('YYYY-MM-DD')"
    :label="label"
    :error="dateError"
  />
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { themeIcons } from 'src/utils/config';
  import WInput from './Input.vue';
  import dayjs from 'dayjs';
  import customParseFormat from 'dayjs/plugin/customParseFormat';
  import utc from 'dayjs/plugin/utc';

  dayjs.extend(customParseFormat);
  dayjs.extend(utc);

  const props = withDefaults(
    defineProps<{
      modelValue: string;
      label?: string;
      loading?: boolean;
      disabled?: boolean;
      onlyPicker?: boolean;
      icon?: string;
      color?: string;
      max?: string;
      error?: string;
    }>(),
    {
      modelValue: '',
      label: '',
      loading: false,
      disabled: false,
      onlyPicker: false,
      icon: 'mdi-calendar-range',
      color: 'secondary',
      max: dayjs().format('YYYY-MM-DD'),
      error: '',
      variant: undefined,
    }
  );
  const emit = defineEmits(['update:modelValue', 'close', 'open', 'error']);

  const collisionDate = computed({
    get: () => {
      const date = formatDate(props.modelValue);
      emit('update:modelValue', date);
      return date;
    },
    set(value) {
      emit('update:modelValue', value);
    },
  });

  const date = ref(dayjs(collisionDate.value).format('YYYY-MM-DD'));
  const dateError = ref(props.error || '');

  function formatDate(date: string) {
    return date
      ? dayjs(date).utc().format('ddd, MMM DD, YYYY')
      : props.onlyPicker
        ? ''
        : dayjs().utc().format('ddd, MMM DD, YYYY');
  }

  function validateDate(val: string) {
    if (dayjs(val, 'YYYY-MM-DD', true).isValid() && dayjs(val).isAfter(dayjs(props.max))) {
      dateError.value = 'Please enter a date before now';
      emit('error', true);
      return false;
    }
    dateError.value = dayjs(val, 'YYYY-MM-DD', true).isValid() ? '' : 'Please enter a valid date';
    emit('error', dayjs(val, 'YYYY-MM-DD', true).isValid() ? false : true);

    return dayjs(val, 'YYYY-MM-DD', true).isValid();
  }

  watch(date, () => {
    if (!validateDate(date.value)) {
      emit('error', true);
      return;
    }
    emit('error', false);
    collisionDate.value = formatDate(`${dayjs(date.value).format('YYYY-MM-DD')}`);
  });

  watch(
    () => props.error,
    () => {
      dateError.value = props.error;
      emit('error', !!dateError.value);
    }
  );
</script>
